import type { ApiPerformanceObserver, ApiRequestPerformanceEvent } from './performance-telemetry';

export type ApiPerformanceReporter = {
  observe: ApiPerformanceObserver;
  flush: () => Promise<void>;
  dispose: () => void;
};

export type ApiPerformanceReporterOptions = {
  endpoint: string;
  getAccessToken: () => string | null;
  fetchImpl?: typeof fetch;
  batchSize?: number;
  flushIntervalMs?: number;
  requestTimeoutMs?: number;
};

const MAX_QUEUE = 40;

/**
 * Creates a bounded, authenticated, best-effort batch reporter. It deliberately
 * uses a raw fetch so reporting a request never recursively reports itself.
 */
export function createApiPerformanceReporter(
  options: ApiPerformanceReporterOptions,
): ApiPerformanceReporter {
  const batchSize = Math.min(20, Math.max(1, options.batchSize ?? 10));
  const flushIntervalMs = Math.max(250, options.flushIntervalMs ?? 5_000);
  const requestTimeoutMs = Math.max(250, options.requestTimeoutMs ?? 5_000);
  const fetchImpl = options.fetchImpl ?? fetch;
  const queue: ApiRequestPerformanceEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let flushing = false;
  let disposed = false;

  const schedule = () => {
    if (timer || disposed) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, flushIntervalMs);
  };

  const observe: ApiPerformanceObserver = (event) => {
    if (disposed) return;
    queue.push({ ...event });
    if (queue.length > MAX_QUEUE) queue.shift();
    if (queue.length >= batchSize) void flush();
    else schedule();
  };

  const flush = async (): Promise<void> => {
    if (disposed || flushing || queue.length === 0) return;
    const token = options.getAccessToken();
    if (!token) return;

    flushing = true;
    const batch = queue.splice(0, batchSize);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      await fetchImpl(options.endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-TraceDocuments-Client-Platform': batch[0]?.platform ?? 'UNKNOWN',
        },
        body: JSON.stringify({ events: batch }),
        signal: controller.signal,
        keepalive: true,
      });
    } catch {
      // Telemetry is intentionally dropped on failure; never retry into a storm.
    } finally {
      clearTimeout(timeoutId);
      flushing = false;
      if (queue.length > 0) schedule();
    }
  };

  const dispose = () => {
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = null;
    queue.length = 0;
  };

  return { observe, flush, dispose };
}
